import React, { useContext } from "react";
import { Controller } from "react-hook-form";
import {
  CREATOR_AND_UP,
  GUEST,
  hasExternalCreator,
  hasInternalCreator,
} from "common/authorization";
import { SUBMISSION_TYPE_MODULE } from "common/constants";
import { spacing } from "../../../../../../stitches.config";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import type { Account } from "../../../../../Authorization/types";
import { FlexColumn } from "../../../../../Common/__styles__/Layout";
import { Title } from "../../../../../Common/Typography";
import { Label } from "../../../../../Inputs";
import {
  Select,
  TagSelect,
  Text,
  Textarea,
} from "../../../../../Inputs/react-hook-form";
import { RichText } from "../../../../../Inputs/RichText";
import { SUBMISSION_CATEGORY_OPTIONS } from "../../../../../Submissions/utils";
import { RoleAndGroupSelector } from "../../SubmissionsBuilder/RoleAndGroupSelector";
import { AttachmentSelect } from "../AttachmentSelect";
import { useBuilderFormContext, useBuilderWatch } from "../form/form";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "../SubmissionsBuilderContext";
import { onCanCreateChange } from "../utils";
import {
  submissionHiddenFromPublicByDefaultToViewerType,
  viewerTypeOptions,
  viewerTypeToSubmissionHiddenFromPublicByDefault,
} from "../viewerType";
import {
  FieldWrapper,
  FormSection,
  FormSectionTitleWrapper,
} from "./__styles__/SidebarColumn";

export function FormSettingsTab() {
  const {
    control,
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useBuilderFormContext();

  const {
    setCanCreateGroups,
    setCanCreateRoles,
    canCreateRoles,
    canCreateGroups,
    existingSubmissionType,
  } = useSubmissionsBuilderContext();

  const [mode] = useSubmissionsBuilderMode();
  const isBuildMode = mode === SUBMISSIONS_BUILDER_MODE.BUILD;

  const { account } = useContext(AuthContext);
  const modules = useBuilderWatch("modules");

  const visibilityDisabled = getIsSisdAndHidden(modules, account);
  const visibilityShown = account?.publicPortal.enabled;

  const accountUserGroups = account?.userGroups ?? [];

  return (
    <FlexColumn css={{ gap: spacing.m }}>
      {isBuildMode && (
        <div>
          <Label text={"All fields are required"} required />
        </div>
      )}

      <Text
        {...register("name", { required: "Record type is required" })}
        label="Record type"
        error={errors.name?.message}
        disabled={!isBuildMode}
      />
      <div>
        <Label text="Record category" htmlFor="category" />
        <Select
          disabled={Boolean(existingSubmissionType) || !isBuildMode}
          control={control}
          name="category"
          options={SUBMISSION_CATEGORY_OPTIONS}
          size="medium"
          rules={{ required: "Record category is required" }}
        />
      </div>

      <FormSection>
        <FormSectionTitleWrapper>
          <Title size="small" type="semiBold">
            Permissions and defaults
          </Title>
        </FormSectionTitleWrapper>
        <FieldWrapper>
          <RoleAndGroupSelector
            control={control}
            options={{
              roles: [...CREATOR_AND_UP, ...GUEST],
              userGroups: accountUserGroups,
            }}
            name="canCreateRolesAndGroupIds"
            error={errors.canCreateRolesAndGroupIds?.message}
            label="Who can fill out this form?"
            disabled={!isBuildMode}
            onChange={() =>
              onCanCreateChange({
                getFormValues: getValues,
                setFormValue: setValue,
                accountUserGroups,
                setCanCreateGroups,
                setCanCreateRoles,
              })
            }
            data-testid="creator-permissions"
          />
        </FieldWrapper>

        {visibilityShown && (
          <FieldWrapper>
            <TagSelect
              control={control}
              name="hiddenFromPublicByDefault"
              options={viewerTypeOptions}
              rules={{
                validate: value => {
                  if (typeof value !== "boolean") {
                    return "This field is required";
                  }
                  return true;
                },
              }}
              error={errors.hiddenFromPublicByDefault?.message}
              disabled={visibilityDisabled || !isBuildMode}
              label="Who can view this form after it is submitted?"
              iconName="eye"
              isMulti={false}
              transform={{
                output: viewerTypeToSubmissionHiddenFromPublicByDefault,
                input: submissionHiddenFromPublicByDefaultToViewerType,
              }}
              data-testid="viewer-permissions"
            />
          </FieldWrapper>
        )}
      </FormSection>

      {/* //TODO: [SUBMISSIONS] this doesn't validate its input; use react hook form version of select? */}
      <AttachmentSelect disabled={!isBuildMode} />

      <Controller
        name="description"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <RichText
              label="Form description"
              name={field.name}
              value={field.value ?? ""}
              onChange={field.onChange}
              error={fieldState.error?.message}
              disabled={!isBuildMode}
            />
          );
        }}
      />

      {hasInternalCreator({ canCreateRoles, canCreateGroups }) && (
        <div>
          <Label text="Success message" htmlFor="successMessage" />
          <Text
            {...register("successMessage", {
              required: "Success message is required",
            })}
            error={errors.successMessage?.message}
            disabled={!isBuildMode}
          />
        </div>
      )}

      {hasExternalCreator({ canCreateRoles, canCreateGroups }) && (
        <>
          <div>
            <Label text="Success page title" htmlFor="preamble" />
            <Text
              {...register("preamble", {
                required: "Success page title is required",
              })}
              error={errors.preamble?.message}
              disabled={!isBuildMode}
            />
          </div>
          <div>
            <Label text="Success page message" htmlFor="postamble" />
            <Text
              {...register("postamble", {
                required: "Success page message is required",
              })}
              error={errors.postamble?.message}
              disabled={!isBuildMode}
            />
          </div>
          <div>
            <Label
              text="Notification emails"
              htmlFor="notificationEmails"
              tooltip="Please enter each email on its own line"
            />
            <Textarea
              {...register("notificationEmails")}
              error={errors.notificationEmails?.message}
              data-testid="notification-emails"
              disabled={!isBuildMode}
            />
          </div>
        </>
      )}
    </FlexColumn>
  );
}

function getIsSisdAndHidden(
  modules: Array<SUBMISSION_TYPE_MODULE>,
  account: Maybe<Account>,
) {
  return (
    modules.includes(
      SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
    ) && !!account?.publicPortal.hideSISD
  );
}
